/**
 * Serviço de informações do dispositivo (equivalente web ao DeviceInfoService do app).
 *
 * - dispositivoId: identificador estável gerado via UUID v5 a partir de uma
 *   fingerprint do dispositivo/navegador (SO + navegador + resolução + idioma, etc).
 *   Diferente de um UUID aleatório, ele é determinístico para a mesma fingerprint,
 *   mas não pode ser o "deviceId real" do Windows/Mac/Linux em um navegador web.
 * - sistemaOperacional: detectado via biblioteca ua-parser-js (SO + versão + navegador).
 * - versaoApp: versão do app injetada pelo Vite a partir do package.json (__APP_VERSION__).
 */

import { UAParser } from 'ua-parser-js';
import { uuidv5, sha256Hex } from '@/utils/uuidv5';

// Namespace fixo para geração de UUID v5 de dispositivos UrbNet.
const DEVICE_NAMESPACE = 'a8d6c2e4-5b3f-4e1a-9c7d-8f2e5b6c4a1d';

export interface DeviceInfo {
  dispositivoId: string;
  dispositivoHash: string;
  sistemaOperacional: string;
  versaoApp: string;
}

/**
 * Monta uma fingerprint estável do dispositivo/navegador.
 * Inclui SO, navegador, plataforma, idioma, resolução e outras características.
 */
function buildDeviceFingerprint(): string {
  let os = 'Desconhecido';
  let browser = 'Desconhecido';
  try {
    const parser = new UAParser();
    const result = parser.getResult();
    os = [result.os.name, result.os.version].filter(Boolean).join(' ') || 'Desconhecido';
    browser = [result.browser.name, result.browser.version].filter(Boolean).join(' ') || 'Desconhecido';
  } catch {
    // fallback abaixo
  }

  const screenInfo = typeof screen !== 'undefined'
    ? `${screen.width}x${screen.height}x${screen.colorDepth}`
    : 'unknown';
  const platform = typeof navigator !== 'undefined' ? navigator.platform || 'unknown' : 'unknown';
  const language = typeof navigator !== 'undefined' ? navigator.language || 'unknown' : 'unknown';
  const cores = typeof navigator !== 'undefined' && navigator.hardwareConcurrency
    ? navigator.hardwareConcurrency
    : 'unknown';
  const touch = typeof navigator !== 'undefined' ? Number(navigator.maxTouchPoints) : 'unknown';

  return [
    `os=${os}`,
    `browser=${browser}`,
    `platform=${platform}`,
    `language=${language}`,
    `screen=${screenInfo}`,
    `cores=${cores}`,
    `touch=${touch}`,
  ].join('|');
}

/**
 * Detecta o sistema operacional legível usando ua-parser-js.
 * Ex.: "Windows 10 (Chrome 124)".
 */
function detectSistemaOperacional(): string {
  try {
    const parser = new UAParser();
    const result = parser.getResult();
    const os = [result.os.name, result.os.version].filter(Boolean).join(' ');
    const browser = [result.browser.name, result.browser.version].filter(Boolean).join(' ');

    if (os && browser) return `${os} (${browser})`;
    if (os) return os;
    if (browser) return browser;
  } catch {
    // fallback abaixo
  }
  return navigator?.userAgent || 'Desconhecido';
}

export const deviceInfoService = {
  /**
   * Retorna informações do dispositivo com identificador baseado em UUID v5.
   * O dispositivoId é determinístico para a mesma fingerprint do navegador.
   */
  async getDeviceInfo(): Promise<DeviceInfo> {
    const fingerprint = buildDeviceFingerprint();
    const dispositivoId = await uuidv5(fingerprint, DEVICE_NAMESPACE);
    const dispositivoHash = await sha256Hex(fingerprint);

    return {
      dispositivoId,
      dispositivoHash,
      sistemaOperacional: detectSistemaOperacional(),
      versaoApp: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0',
    };
  },
};
